import type { SerializedEditorState } from "lexical";
import { ValidationError } from "yup";
import { imageFetcher } from "../../../utils/server-fetcher";
import { isChatChannel } from "../../channels/types";
import { imageSizeLimit } from "../../lexical-chat-moment/image-size-limit";
import type { Tags } from "../../merge-tag/types";
import { validMergeTags } from "../../merge-tag/util";
import { BroadcastNode } from "../../message/editor/nodes/broadcast-node";
import type { SerializedButtonListNode } from "../../message/editor/nodes/button-list-node";
import { ChannelNode } from "../../message/editor/nodes/channel/channel-node";
import type { SerializedDesignHuddleNode } from "../../message/editor/nodes/design-huddle-node";
import type { SerializedImageNode } from "../../message/editor/nodes/image-node";
import type { SerializedVideoNode } from "../../message/editor/nodes/video-node";
import { getCurlyBraceBalance } from "../../message/editor/plugins/typeahead/merge-tag-utils";
import { editorIsEmpty } from "../../message/editor/utils/editor-is-empty";
import type { ToTarget } from "../../to/use-to-target";

type Node = {
  type: string;
  children?: Node[];
  text?: string;
  key?: string;
};

const TOP_LEVEL_NODES = [
  "paragraph",
  "list",
  "image",
  "divider",
  "buttonlist",
  "design-huddle",
  "heading",
  "dynamic",
  "poll",
  "video",
];

const TEXT_LIKE_NODES = [
  "text",
  "link",
  "autolink",
  "merge-tag",
  "emoji",
  "linebreak",
  "tab",
  "merge-tag-v2",
  "custom-emoji",
  "surveylink",
];

const ALLOWED_NODE_NESTING: { [key: string]: string[] } = {
  root: TOP_LEVEL_NODES,
  dynamic: [...TOP_LEVEL_NODES.filter((x) => x !== "dynamic"), "dynamic-toolbar"],
  paragraph: [...TEXT_LIKE_NODES],
  list: ["listitem"],
  listitem: [...TEXT_LIKE_NODES, "list"],
  link: ["text", "merge-tag", "emoji", "linebreak", "tab", "merge-tag-v2"],
  autolink: ["text", "merge-tag", "emoji", "linebreak", "tab"],
  heading: [...TEXT_LIKE_NODES],
  surveylink: ["text"],
} as const;

const TOP_LEVEL_NODES_EMAIL = [
  ...TOP_LEVEL_NODES,
  "styleablebutton",
  "table",
  "paper-color",
  "data-table",
];

// TODO styleable_columns this logic works for both flag on and off.
const tablecell = TOP_LEVEL_NODES_EMAIL.filter((x) => x !== "table");

const ALLOWED_EMAIL: { [key: string]: string[] } = {
  ...ALLOWED_NODE_NESTING,
  paragraph: ALLOWED_NODE_NESTING.paragraph?.filter((x) => x !== "custom-emoji") ?? [],
  listitem: ALLOWED_NODE_NESTING.listitem?.filter((x) => x !== "custom-emoji") ?? [],
  root: TOP_LEVEL_NODES_EMAIL,
  dynamic: [...TOP_LEVEL_NODES_EMAIL, "dynamic-toolbar"],
  table: ["tablerow"],
  tablerow: ["tablecell", "stylabletablecell", "data-table-cell"],
  "data-table": ["tablerow"],
  tablecell,
  stylabletablecell: tablecell,
  "data-table-cell": [...TEXT_LIKE_NODES, "heading", "list"],
};

export const ALLOWED_CHAT_NODES: { [key: string]: string[] } = {
  ...ALLOWED_NODE_NESTING,
  paragraph: [
    ...(ALLOWED_NODE_NESTING.paragraph ?? []),
    ChannelNode.getType(),
    BroadcastNode.getType(),
  ],
};

const ALLOWED_CHAT_CHANNEL_NODES: { [key: string]: string[] } = {
  ...ALLOWED_CHAT_NODES,
  root: [...TOP_LEVEL_NODES.filter((x) => x !== "dynamic")],
};

const noRecursiveColumnMessage =
  "Message not supported, Columns cannot be nested within other Columns";
const INVALID_REASONS = [
  {
    parent: "tablecell",
    child: "table",
    message: noRecursiveColumnMessage,
  },
  {
    parent: "stylabletablecell",
    child: "table",
    message: noRecursiveColumnMessage,
  },
  {
    parent: "root",
    child: "dynamic",
    message: "Dynamic content cannot be used on a Channel communication",
  },
];

const determineAllowedNodes = (
  channel: string | undefined,
  conditions: { target_channel?: boolean } = {},
): {
  [key: string]: string[];
} => {
  if (channel === "work_email" || channel === "personal_email") {
    return ALLOWED_EMAIL;
  }

  if ((channel === "slack" || channel === "teams") && conditions.target_channel) {
    return ALLOWED_CHAT_CHANNEL_NODES;
  }
  if (channel === "slack" || channel === "teams") {
    return ALLOWED_CHAT_NODES;
  }

  return ALLOWED_NODE_NESTING;
};

export const isValidNest = (
  { type, children }: Node,
  channel: string | undefined,
  conditions: { target_channel?: boolean } = {},
): { valid: true } | { valid: false; invalidReason: string; key?: string } => {
  const allowedNodes = determineAllowedNodes(channel, conditions);

  if (type === "link") {
    const hasTextChild = children?.some(
      (child) => child.type === "text" && child.text && child.text.length > 0,
    );
    if (!hasTextChild) {
      return {
        valid: false,
        invalidReason:
          "Corrupted Communication: Link found without any text content. Please contact support.",
        key: undefined,
      };
    }
  }

  for (const child of children ?? []) {
    if (!(type in allowedNodes && allowedNodes[type]?.includes(child.type))) {
      console.log("Invalid nest", type, child.type);

      const invalidReason =
        INVALID_REASONS.find((x) => x.parent === type && x.child === child.type)?.message ??
        "Message not supported, please simplify the layout";
      return { valid: false, invalidReason, key: child.key };
    }

    const validNest = isValidNest(child, channel, conditions);
    if (!validNest.valid) {
      return validNest;
    }
  }

  return { valid: true };
};

const checkMergeTagBalance = ({ type, children, text }: Node): boolean => {
  if (type === "text") return getCurlyBraceBalance(text as string).balanced;

  return children?.every((child) => checkMergeTagBalance(child)) ?? true;
};

const checkButtonListContainButtons = (node: Node): boolean => {
  if (node.type === "buttonlist") {
    const buttonNode = node as SerializedButtonListNode;
    return buttonNode.buttons.length !== 0;
  }

  return node.children?.every((child) => checkButtonListContainButtons(child)) ?? true;
};

const checkGifFileSize = async (node: Node, channel: string | undefined): Promise<boolean> => {
  if (!channel || !isChatChannel(channel)) return true;
  const allGifImages =
    (node.children?.filter(
      (child) => child.type === "image" && (child as SerializedImageNode).url.endsWith(".gif"),
    ) as SerializedImageNode[]) ?? [];

  const responses = await Promise.all(
    allGifImages.map((gif) =>
      (gif.url.includes("changeengine.com") ? imageFetcher : fetch)(gif.url, { method: "HEAD" }),
    ),
  );

  return responses.every((response) => {
    const size = response.headers.get("content-length") as unknown as number;
    console.log("Sending .gif of size: ", size);

    if (!size || !response.ok) return false;

    const sizeInMb = size / 1024 / 1024;
    return sizeInMb < imageSizeLimit(channel);
  });
};

export const validateMomentDesign = (
  design: unknown,
  channel: string | undefined,
  createError: (message: string) => ValidationError,
  target_channel: boolean,
): boolean | ValidationError | Promise<boolean | ValidationError> => {
  const serializedState = design as SerializedEditorState;
  if (editorIsEmpty(serializedState)) {
    return createError("Enter a message");
  }

  const validNest = isValidNest(serializedState.root, channel, {
    target_channel,
  });
  if (validNest.valid === false) {
    console.error("Unsupported message", serializedState.root);
    return createError(validNest.invalidReason);
  }
  if (!checkMergeTagBalance(serializedState.root)) {
    return createError("Unbalanced merge tags found, ensure all merge tags are closed properly");
  }
  if (!checkButtonListContainButtons(serializedState.root)) {
    return createError(
      "ButtonLists found without Buttons, please ensure all ButtonLists contain at least one Button",
    );
  }

  return checkGifFileSize(serializedState.root, channel).then((validSize) => {
    if (validSize) return true;

    return createError(
      `GIF in message content is too large, ensure all images are less than ${imageSizeLimit(channel as "teams" | "slack")}MB`,
    );
  });
};

export const validateLexicalMergeTags = (
  design: unknown,
  mergeTags: Tags,
  toTarget: ToTarget,
  createError: (message: string) => ValidationError,
): boolean | ValidationError => {
  const serializedState = design as SerializedEditorState;

  const tags = validMergeTags(mergeTags, toTarget).map((t) => t.id);
  const invalidTags: string[] = [];

  const isValidMergeTags = ({ type, children, text }: Node): void => {
    if (type === "merge-tag-v2" && text && !tags.includes(text)) {
      invalidTags.push(text);
    }

    if (children) {
      for (const child of children) {
        isValidMergeTags(child);
      }
    }
  };

  isValidMergeTags(serializedState.root);

  if (invalidTags.length > 0) {
    return createError(
      `Invalid merge tags: ${invalidTags
        .map((t) => `{{${t}}}`)
        .join(", ")
        .replace(/,(?!.*,)/gim, " and")}`,
    );
  }

  return true;
};

export const validateMergeTags = (
  body: string,
  merge_tags: Tags,
  toTarget: ToTarget,
  createError: (message: string) => ValidationError,
): boolean | ValidationError => {
  const tags = validMergeTags(merge_tags, toTarget).map((t) => t.id);

  const invalid = getInvalidTags(body, tags);

  if (invalid.length > 0) {
    return createError(
      `Invalid merge tags: ${invalid
        .map((t) => `{{${t}}}`)
        .join(", ")
        .replace(/,(?!.*,)/gim, " and")}`,
    );
  }

  return true;
};

const getInvalidTags = (value: string, mergeTags: string[]): string[] => {
  let match: RegExpExecArray | null;
  const invalidTags: string[] = [];

  // 1. Left of | : Match {{#jwt}}...{{/jwt}}
  // 2. Middle of | : Match {{#if...}}...{{/if}} (handles optional args like {{#if condition}})
  // 3. Right of | : Match {{key}} (captures "key" in group 1)
  const mergeTagRegex = /{{#jwt}}[\s\S]*?{{\/jwt}}|{{#if.*?}}[\s\S]*?{{\/if}}|{{(.*?)}}/g;

  while ((match = mergeTagRegex.exec(value ?? "")) !== null) {
    const matchOne = match[1];

    // If matchOne is defined, it's a standard tag captured by group 1.
    // If undefined, it was a {{#jwt}} or {{#if}} block, so we loop again.
    if (matchOne) {
      if (!mergeTags.includes(matchOne) && !matchOne.startsWith("hyperlink:")) {
        invalidTags.push(matchOne);
      }
    }
  }
  return invalidTags;
};

export const validateDesignHuddleNodeImages = (
  design: unknown,
  createError: (message: string) => ValidationError,
): boolean | ValidationError => {
  const serializedState = design as SerializedEditorState;

  const result = serializedState.root.children.some((node: Node) => {
    if (node.type === "design-huddle") {
      const dhNode = node as SerializedDesignHuddleNode;
      if (dhNode.version === 2 && dhNode.error) {
        return true;
      }
    }
  });

  if (result) return createError("Invalid images found");

  return true;
};

export const validateVideoNodes = (
  design: unknown,
  createError: (message: string) => ValidationError,
): boolean | ValidationError => {
  const serializedState = design as SerializedEditorState;

  const result = serializedState.root.children.find((node: Node) => {
    if (node.type === "video") {
      const videoNode = node as SerializedVideoNode;
      return !videoNode.title || videoNode.title?.length > 1999 ? videoNode : undefined;
    }

    if (node.type === "design-huddle") {
      const dhNode = node as SerializedDesignHuddleNode;
      if (dhNode.version === 2 && dhNode.contentType === "video")
        return !dhNode.title || dhNode.title?.length > 1999 ? dhNode : undefined;
    }
  }) as SerializedVideoNode;

  if (result)
    return createError(
      result.title
        ? "Video title must be less than 2000 characters"
        : "Enter a title for the video",
    );

  return true;
};
